import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, Plug, Search, Sparkles } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/Card";
import { StatusBadge, Badge } from "../components/ui/Badge";
import { Button, buttonVariants } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { EmptyState } from "../components/ui/EmptyState";
import { useAuth } from "../lib/auth";
import { useConnections } from "../lib/hooks";

export default function Connections() {
  const { currentTenant } = useAuth();
  const { data, loading, error, reload } = useConnections(currentTenant?.slug);
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const list = data ?? [];
    const term = q.trim().toLowerCase();
    if (!term) return list;
    return list.filter(
      (c) =>
        c.display_name.toLowerCase().includes(term) ||
        c.system_type.toLowerCase().includes(term) ||
        c.slug.toLowerCase().includes(term),
    );
  }, [data, q]);

  return (
    <>
      <PageHeader
        title="Connections"
        description="External systems wired up for this workspace, each registered as an agent tool."
        actions={
          <Link to="/app/connections/new" className={buttonVariants()}>
            <Sparkles className="h-4 w-4" /> New integration
          </Link>
        }
      />

      {data && data.length > 0 && (
        <div className="relative mb-6 max-w-sm">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search connections…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-10"
          />
        </div>
      )}

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Card key={i} className="p-5">
              <div className="skeleton h-10 w-10 rounded-xl" />
              <div className="skeleton mt-4 h-4 w-32" />
              <div className="skeleton mt-2 h-3 w-20" />
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card className="flex flex-col items-center gap-3 px-6 py-14 text-center">
          <AlertCircle className="h-8 w-8 text-amber-500" />
          <div>
            <p className="font-semibold text-slate-800">Couldn't load connections</p>
            <p className="mt-1 text-sm text-slate-400">{error}</p>
          </div>
          <Button variant="outline" size="sm" onClick={reload}>
            Retry
          </Button>
        </Card>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Plug className="h-6 w-6" />}
          title={q ? "No matches" : "No connections yet"}
          description={
            q
              ? "Try a different search term."
              : "Describe a system in plain English and Wise 2.0 will generate, test, and register the connector."
          }
          action={
            !q && (
              <Link to="/app/connections/new" className={buttonVariants()}>
                <Sparkles className="h-4 w-4" /> Create your first
              </Link>
            )
          }
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((c) => (
            <Link
              key={c.id}
              to={`/app/connections/${c.slug}`}
              className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-card transition-all hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-lift"
            >
              <div className="flex items-start justify-between">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-500">
                  <Plug className="h-5 w-5" />
                </span>
                <StatusBadge status={c.status} />
              </div>
              <h3 className="mt-4 truncate text-base font-semibold text-slate-900">{c.display_name}</h3>
              <p className="text-sm text-slate-400">{c.system_type}</p>
              {c.capabilities.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {c.capabilities.slice(0, 3).map((cap) => (
                    <Badge key={cap} tone="slate">
                      {cap}
                    </Badge>
                  ))}
                  {c.capabilities.length > 3 && (
                    <Badge tone="slate">+{c.capabilities.length - 3}</Badge>
                  )}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </>
  );
}
